import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { getContent } from '@plone/volto/actions/content/content';
import { getQueryStringResults } from '@plone/volto/actions/querystringsearch/querystringsearch';
import { usePagination } from '@plone/volto/helpers/Utils/usePagination';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import {
  computeTotalPages,
  computeVisibleTotal,
} from '@plone/volto/helpers/Pagination/Pagination';

import config from '@plone/volto/registry';

import {
  buildUnpaginatedListingQuery,
  getEffectiveListingBatchSize,
  shouldDisablePagination,
  shouldUseQuerystringListing,
  usesCustomListing,
} from '../../../../../helpers/listingPagination';

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default function withQuerystringResults(WrappedComponent) {
  function WithQuerystringResults(props) {
    const {
      data = {},
      id = data.block,
      properties: content,
      path,
      variation,
    } = props;
    const { settings } = config;
    const querystring = data.querystring || data;
    const subrequestID = content?.UID ? `${content?.UID}-${id}` : id;
    const { b_size = settings.defaultPageSize } = querystring;
    const disablePagination = shouldDisablePagination(data);
    const customListing = usesCustomListing(data);
    const effectiveBSize = getEffectiveListingBatchSize(data, b_size);

    const [initialPath] = React.useState(getBaseUrl(path));

    const copyFields = ['limit', 'query', 'sort_on', 'sort_order', 'depth'];
    const { currentPage, setCurrentPage } = usePagination(id, 1);
    const folderItems = content?.is_folderish ? content.items : [];
    const hasQuery = querystring?.query?.length > 0;
    const adaptedQuery = disablePagination
      ? Object.assign(
          variation?.fullobjects
            ? { fullobjects: 1 }
            : { metadata_fields: '_all' },
          buildUnpaginatedListingQuery(querystring, hasQuery),
        )
      : Object.assign(
          variation?.fullobjects
            ? { fullobjects: 1 }
            : { metadata_fields: '_all' },
          {
            b_size: effectiveBSize,
            offset: querystring?.offset || 0,
          },
          ...copyFields.map((name) =>
            Object.keys(querystring).includes(name)
              ? { [name]: querystring[name] }
              : {},
          ),
        );
    const adaptedQueryRef = useRef(adaptedQuery);
    const currentPageRef = useRef(currentPage);

    const querystringResults = useSelector(
      (state) => state.querystringsearch.subrequests,
    );
    const dispatch = useDispatch();

    const useQuerystringListing =
      !customListing && shouldUseQuerystringListing(data, hasQuery);
    const hasLoaded = customListing
      ? true
      : useQuerystringListing
        ? querystringResults?.[subrequestID]?.loaded
        : true;

    const listingItems = customListing
      ? []
      : useQuerystringListing
        ? querystringResults?.[subrequestID]?.items || []
        : folderItems;

    const subrequestTotal = querystringResults?.[subrequestID]?.total;
    const visibleTotal = computeVisibleTotal(
      subrequestTotal,
      adaptedQuery.offset,
    );

    const showAsFolderListing =
      !disablePagination && !hasQuery && content?.items_total > effectiveBSize;
    const showAsQueryListing =
      !disablePagination && hasQuery && visibleTotal > effectiveBSize;

    const totalPages = customListing
      ? 0
      : disablePagination
        ? 0
        : showAsFolderListing
          ? computeTotalPages(content.items_total, effectiveBSize)
          : showAsQueryListing
            ? computeTotalPages(visibleTotal, effectiveBSize)
            : 0;

    const prevBatch = disablePagination
      ? null
      : showAsFolderListing
        ? content.batching?.prev
        : showAsQueryListing
          ? querystringResults[subrequestID].batching?.prev
          : null;
    const nextBatch = disablePagination
      ? null
      : showAsFolderListing
        ? content.batching?.next
        : showAsQueryListing
          ? querystringResults[subrequestID].batching?.next
          : null;

    const isImageGallery =
      (!data.variation && data.template === 'imageGallery') ||
      data.variation === 'imageGallery';

    useDeepCompareEffect(() => {
      if (customListing) {
        return;
      }

      if (hasQuery) {
        dispatch(
          getQueryStringResults(
            initialPath,
            adaptedQuery,
            subrequestID,
            disablePagination ? undefined : currentPage,
          ),
        );
      } else if (isImageGallery && !hasQuery) {
        dispatch(
          getQueryStringResults(
            initialPath,
            {
              ...adaptedQuery,
              b_size: 10000000000,
              query: [
                {
                  i: 'path',
                  o: 'plone.app.querystring.operation.string.relativePath',
                  v: '',
                },
              ],
            },
            subrequestID,
          ),
        );
      } else if (disablePagination) {
        dispatch(
          getQueryStringResults(initialPath, adaptedQuery, subrequestID),
        );
      } else {
        dispatch(getContent(initialPath, null, null, currentPage));
      }
      adaptedQueryRef.current = adaptedQuery;
      currentPageRef.current = currentPage;
    }, [
      subrequestID,
      isImageGallery,
      adaptedQuery,
      hasQuery,
      disablePagination,
      customListing,
      initialPath,
      dispatch,
      currentPage,
    ]);

    return (
      <WrappedComponent
        {...props}
        onPaginationChange={(e, { activePage }) => setCurrentPage(activePage)}
        total={subrequestTotal === undefined ? undefined : visibleTotal}
        batch_size={effectiveBSize}
        currentPage={currentPage}
        totalPages={totalPages}
        prevBatch={prevBatch}
        nextBatch={nextBatch}
        listingItems={listingItems}
        hasLoaded={hasLoaded}
        isFolderContentsListing={showAsFolderListing}
      />
    );
  }

  WithQuerystringResults.displayName = `WithQuerystringResults(${getDisplayName(
    WrappedComponent,
  )})`;

  return hoistNonReactStatics(WithQuerystringResults, WrappedComponent);
}
